import { __spreadArray, __extends, __assign, __rest } from 'tslib';
import React__default from 'react';
import 'react-dates/initialize';
import { DayPickerRangeController } from 'react-dates';
import moment from 'moment-timezone';
import clsx from 'clsx';
import Radio from '../Radio/Radio.component.js';
import Text from '../Text/Text.component.js';
import { Icon } from '../Icon/Icon.component.js';
import DateRangePickerInline from '../DateRangePickerInline/DateRangePickerInline.component.js';
import { propTypes, defaultProps } from './DateRangeFilterInline.types.js';
import { getStateFromNewSelectedRange, getTimezone } from './DateRangeFilterInline.utils.js';
import './DateRangeFilterInline.scss.js';
import styles from './DateRangeFilterInline.module.scss.js';

var TODAY = 'today';
var THIS_WEEK = 'thisWeek';
var LAST_WEEK = 'lastWeek';
var THIS_MONTH = 'thisMonth';
var LAST_MONTH = 'lastMonth';
var CUSTOM = 'custom';
var DATE_RANGE_FILTERS = [
    THIS_MONTH,
    TODAY,
    LAST_WEEK,
    THIS_WEEK,
    LAST_MONTH,
];
var DATE_RANGE_FILTERS_WITH_CUSTOM = __spreadArray([CUSTOM], DATE_RANGE_FILTERS, true);
var DateRangeFilterInline = /** @class */ (function (_super) {
    __extends(DateRangeFilterInline, _super);
    function DateRangeFilterInline(props) {
        var _this = _super.call(this, props) || this;
        _this.handleDateChange = function (event) {
            var _a = _this.props, startDateInputRef = _a.startDateInputRef, dateFormat = _a.dateFormat;
            var target = event.target;
            var value = target.value;
            var _b = getStateFromNewSelectedRange(value, _this.timezone), startDate = _b.startDate, endDate = _b.endDate, selectedRange = _b.selectedRange, focusedInput = _b.focusedInput;
            _this.setState({
                startDate: startDate,
                endDate: endDate,
                startDateInput: startDate ? startDate.format(dateFormat) : '',
                endDateInput: endDate ? endDate.format(dateFormat) : '',
                selectedRange: selectedRange,
                focusedInput: focusedInput,
            }, function () {
                if (selectedRange === CUSTOM && !!(startDateInputRef === null || startDateInputRef === void 0 ? void 0 : startDateInputRef.current)) {
                    startDateInputRef.current.focus({
                        preventScroll: true,
                    });
                }
                if (selectedRange !== CUSTOM) {
                    _this.handleApplyFilters();
                }
                else {
                    _this.props.onDatesChange({
                        startDate: _this.state.startDate || null,
                        endDate: _this.state.endDate || null,
                        selectedRange: selectedRange,
                    });
                }
            });
        };
        _this.handleCustomRangeDateChange = function (_a) {
            var startDate = _a.startDate, endDate = _a.endDate, name = _a.name;
            return function () {
                var _a = _this.props, dateFormat = _a.dateFormat, startDateInputRef = _a.startDateInputRef;
                if (!startDate || !endDate || !name) {
                    return console.error('One of the custom range parameters is not valid');
                }
                var momentStartDate = moment(startDate);
                var momentEndDate = moment(endDate);
                _this.setState({
                    startDate: startDate
                        ? momentStartDate.startOf('day')
                        : null,
                    endDate: endDate ? momentEndDate.endOf('day') : null,
                    startDateInput: startDate
                        ? momentStartDate.format(dateFormat)
                        : '',
                    endDateInput: endDate
                        ? momentEndDate.format(dateFormat)
                        : '',
                    selectedCustomRange: { name: name, endDate: endDate, startDate: startDate },
                    focusedInput: name === CUSTOM ? 'startDate' : null,
                    selectedRange: null,
                    startDateInputInvalid: false,
                    endDateInputInvalid: false,
                }, function () {
                    if (name === CUSTOM && !!(startDateInputRef === null || startDateInputRef === void 0 ? void 0 : startDateInputRef.current)) {
                        startDateInputRef.current.focus({
                            preventScroll: true,
                        });
                    }
                    if (name !== CUSTOM) {
                        _this.handleApplyFilters();
                    }
                });
            };
        };
        _this.handleCustomDateChange = function (_a) {
            var startDate = _a.startDate, endDate = _a.endDate;
            var _b = _this.props, startDateInputRef = _b.startDateInputRef, endDateInputRef = _b.endDateInputRef, dateFormat = _b.dateFormat, applyCustomOnChange = _b.applyCustomOnChange;
            var formattedStartDate = '';
            var formattedEndDate = '';
            if (startDate) {
                formattedStartDate = startDate.format(dateFormat);
            }
            if (endDate) {
                formattedEndDate = endDate.format(dateFormat);
            }
            _this.setState({
                startDate: startDate ? moment(startDate).startOf('day') : null,
                endDate: endDate ? moment(endDate).endOf('day') : null,
                startDateInput: formattedStartDate,
                endDateInput: formattedEndDate,
                selectedRange: CUSTOM,
                startDateInputInvalid: false,
                endDateInputInvalid: false,
            }, function () {
                _this.fixStartDay();
                if (_this.state.focusedInput === 'startDate' &&
                    !!(startDateInputRef === null || startDateInputRef === void 0 ? void 0 : startDateInputRef.current)) {
                    startDateInputRef.current.focus({
                        preventScroll: true,
                    });
                }
                if (_this.state.focusedInput === 'endDate' &&
                    !!(endDateInputRef === null || endDateInputRef === void 0 ? void 0 : endDateInputRef.current)) {
                    endDateInputRef.current.focus({
                        preventScroll: true,
                    });
                }
                if (applyCustomOnChange) {
                    _this.handleApplyFilters();
                }
                else {
                    _this.props.getData(_this.state);
                }
            });
        };
        _this.handleApplyFilters = function () {
            _this.props.onDatesChange({
                startDate: _this.state.startDate || null,
                endDate: _this.state.endDate || null,
                selectedRange: _this.state.selectedRange,
                customSelectedRange: _this.state.selectedCustomRange,
            });
        };
        _this.handleStartDateInputChange = function (e) {
            var _a = _this.props, isDayBlocked = _a.isDayBlocked, applyCustomOnChange = _a.applyCustomOnChange;
            var target = e.target;
            var newState = {
                startDateInput: target.value,
                startDateInputInvalid: false,
                endDateInputInvalid: false,
            };
            var parsedStartDate = moment(target.value, _this.props.inputDateFormats, true).tz(_this.timezone, true);
            var parsedEndDate = moment(_this.state.endDateInput, _this.props.inputDateFormats, true).tz(_this.timezone, true);
            var isStartDayBlocked = isDayBlocked
                ? isDayBlocked(parsedStartDate)
                : false;
            if (parsedStartDate.isValid() && !isStartDayBlocked) {
                if (parsedEndDate.isValid()) {
                    newState['endDateInputInvalid'] = false;
                    if (parsedStartDate.isSameOrBefore(parsedEndDate)) {
                        newState['startDate'] = parsedStartDate;
                        newState['endDate'] = parsedEndDate;
                    }
                    else {
                        newState['startDateInputInvalid'] = true;
                    }
                }
                else {
                    if (parsedStartDate.isSameOrBefore(_this.state.endDate)) {
                        newState['startDate'] = parsedStartDate;
                    }
                }
            }
            else {
                newState['startDateInputInvalid'] = true;
            }
            _this.setState(newState, function () {
                _this.fixStartDay();
                if (applyCustomOnChange) {
                    _this.handleApplyFilters();
                }
                else {
                    _this.props.getData(_this.state);
                }
            });
        };
        _this.handleEndDateInputChange = function (e) {
            var _a = _this.props, isDayBlocked = _a.isDayBlocked, applyCustomOnChange = _a.applyCustomOnChange;
            var target = e.target;
            var newState = {
                endDateInput: target.value,
                endDateInputInvalid: false,
            };
            var parsedEndDate = moment(target.value, _this.props.inputDateFormats, true).tz(_this.timezone, true);
            var parsedStartDate = moment(_this.state.startDateInput, _this.props.inputDateFormats, true).tz(_this.timezone, true);
            var endDateBlocked = isDayBlocked
                ? isDayBlocked(parsedEndDate)
                : false;
            if (parsedEndDate.isValid() && !endDateBlocked) {
                if (parsedStartDate.isValid()) {
                    newState['startDateInputInvalid'] = false;
                    if (parsedEndDate.isSameOrAfter(parsedStartDate)) {
                        newState['endDate'] = parsedEndDate;
                        newState['startDate'] = parsedStartDate;
                    }
                    else {
                        newState['endDateInputInvalid'] = true;
                    }
                }
                else {
                    if (parsedEndDate.isSameOrAfter(_this.state.startDate)) {
                        newState['endDate'] = parsedEndDate;
                    }
                }
            }
            else {
                newState['endDateInputInvalid'] = true;
            }
            _this.setState(newState, function () {
                _this.fixStartDay();
                if (applyCustomOnChange) {
                    _this.handleApplyFilters();
                }
                else {
                    _this.props.getData(_this.state);
                }
            });
        };
        _this.handleStartDateInputFocus = function () {
            _this.setState({ focusedInput: 'startDate' });
        };
        _this.handleEndDateInputFocus = function () {
            _this.setState({ focusedInput: 'endDate' });
        };
        _this.handleFocusChange = function (focusedInput) {
            _this.setState({ focusedInput: focusedInput });
        };
        /**
         * There is a bug in the DRP from react-dates where a class
         * is not being removed when selecting an end date. This solution
         * seeks to find the element with the classname that causes the visual
         * bug, and removes it.
         * */
        _this.fixStartDay = function () {
            if (_this.state.startDate && _this.state.endDate) {
                var startDateElms = document.querySelectorAll('.CalendarDay__selected_start_no_selected_end');
                startDateElms.forEach(function (startDateElm) {
                    startDateElm.classList.remove('CalendarDay__selected_start_no_selected_end');
                });
            }
        };
        _this.renderMonthElement = function (_a) {
            var month = _a.month;
            month.locale(_this.props.calendarLocale);
            return React__default.createElement(Text, { type: "sub1" }, month.format('MMMM YYYY'));
        };
        _this.renderDayContents = function (day) {
            day.locale(_this.props.calendarLocale);
            var dayClass = 'SingleDate';
            var isFirstDayOfMonth = day.date() === 1;
            var isLastDayOfMonth = day.date() === day.daysInMonth();
            if (isFirstDayOfMonth) {
                dayClass += ' firstDayOfMonth';
            }
            if (isLastDayOfMonth) {
                dayClass += ' lastDayOfMonth';
            }
            return (React__default.createElement("div", { className: dayClass },
                React__default.createElement("span", { className: "SingleDate_date" }, day.format('D'))));
        };
        _this.isCustomDatePickerRender = function () {
            var _a;
            return (!!_this.props.customDateRanges &&
                !_this.props.customDateRanges.length) ||
                ((_this.state.selectedRange === CUSTOM ||
                    ((_a = _this.state.selectedCustomRange) === null || _a === void 0 ? void 0 : _a.name) === CUSTOM) &&
                    _this.props.isCustomRangeShowed);
        };
        _this.checkIfCustomArray = function () {
            var _a = _this.props, isCustomRangeShowed = _a.isCustomRangeShowed, customDateRanges = _a.customDateRanges;
            if (isCustomRangeShowed && !!(customDateRanges === null || customDateRanges === void 0 ? void 0 : customDateRanges.length)) {
                return __spreadArray([
                    {
                        name: CUSTOM,
                        startDate: moment().tz(_this.timezone, true).startOf('day'),
                        endDate: moment().tz(_this.timezone, true).endOf('day'),
                    }
                ], customDateRanges, true);
            }
            return customDateRanges || [];
        };
        var _a = _this.props, selectedRange = _a.selectedRange, customSelectedRange = _a.customSelectedRange;
        var _b = _this.props, startDate = _b.startDate, endDate = _b.endDate, customDateRanges = _b.customDateRanges, timeZone = _b.timeZone;
        _this.timezone = getTimezone(timeZone);
        var focusedInput = 'startDate';
        var stateStartDate = startDate && moment(startDate);
        var stateEndDate = endDate && moment(endDate);
        if (!startDate || !endDate) {
            if (customDateRanges && customDateRanges.length > 0) {
                customSelectedRange = __assign({}, customDateRanges[0]);
                stateStartDate = moment(customDateRanges[0].startDate);
                stateEndDate = moment(customDateRanges[0].endDate);
            }
            else if (selectedRange && !customDateRanges) {
                var newState = getStateFromNewSelectedRange(selectedRange, _this.timezone);
                stateStartDate = newState.startDate;
                stateEndDate = newState.endDate;
                selectedRange = newState.selectedRange;
                focusedInput = newState.focusedInput;
            }
            else {
                stateStartDate = moment()
                    .tz(_this.timezone, true)
                    .startOf('day');
                stateEndDate = moment().tz(_this.timezone, true).endOf('day');
                selectedRange = TODAY;
                focusedInput = 'startDate';
            }
        }
        _this.state = {
            focusedInput: focusedInput,
            selectedCustomRange: customDateRanges ? customSelectedRange : null,
            selectedRange: customDateRanges ? null : selectedRange || CUSTOM,
            startDateInputInvalid: false,
            endDateInputInvalid: false,
            startDate: stateStartDate,
            endDate: stateEndDate,
            startDateInput: stateStartDate
                ? stateStartDate.format(props.inputDateFormats[0])
                : '',
            endDateInput: stateEndDate
                ? stateEndDate.format(props.inputDateFormats[0])
                : '',
        };
        return _this;
    }
    DateRangeFilterInline.prototype.componentDidUpdate = function (prevProps) {
        var _a, _b, _c, _d, _e, _f;
        var newState = {};
        if (this.props.timeZone !== this.timezone) {
            this.timezone = getTimezone(this.props.timeZone);
        }
        if (this.props.startDate !== prevProps.startDate) {
            var startDate = this.props.startDate
                ? moment(this.props.startDate)
                : null;
            newState['startDate'] = startDate;
        }
        if (this.props.endDate !== prevProps.endDate) {
            var endDate = this.props.endDate
                ? moment(this.props.endDate)
                : null;
            newState['endDate'] = endDate;
        }
        if (((_a = this.props.customSelectedRange) === null || _a === void 0 ? void 0 : _a.name) !==
            ((_b = prevProps.customSelectedRange) === null || _b === void 0 ? void 0 : _b.name) ||
            ((_c = this.props.customSelectedRange) === null || _c === void 0 ? void 0 : _c.startDate) !==
                ((_d = prevProps.customSelectedRange) === null || _d === void 0 ? void 0 : _d.startDate) ||
            ((_e = this.props.customSelectedRange) === null || _e === void 0 ? void 0 : _e.endDate) !==
                ((_f = prevProps.customSelectedRange) === null || _f === void 0 ? void 0 : _f.endDate)) {
            newState['selectedCustomRange'] = this.props.customSelectedRange;
        }
        if (this.props.selectedRange !== prevProps.selectedRange) {
            newState['selectedRange'] = this.props.selectedRange;
        }
        if (Object.keys(newState).length > 0) {
            this.setState(newState);
        }
    };
    DateRangeFilterInline.prototype.render = function () {
        var _this = this;
        var _a = this.state, startDateInputInvalid = _a.startDateInputInvalid, endDateInputInvalid = _a.endDateInputInvalid, startDate = _a.startDate, endDate = _a.endDate, selectedRange = _a.selectedRange, selectedCustomRange = _a.selectedCustomRange, focusedInput = _a.focusedInput, startDateInput = _a.startDateInput, endDateInput = _a.endDateInput;
        var _b = this.props, isDayBlocked = _b.isDayBlocked, dateLabels = _b.dateLabels, rangeLabels = _b.rangeLabels, startDateInputRef = _b.startDateInputRef, endDateInputRef = _b.endDateInputRef, invalidInputErrorMessage = _b.invalidInputErrorMessage, calendarProps = _b.calendarProps, customDateRanges = _b.customDateRanges, isCustomRangeShowed = _b.isCustomRangeShowed, className = _b.className;
        var onPrevMonthClick = calendarProps.onPrevMonthClick, onNextMonthClick = calendarProps.onNextMonthClick, isDayHighlighted = calendarProps.isDayHighlighted, renderDayContents = calendarProps.renderDayContents, initialVisibleMonth = calendarProps.initialVisibleMonth, renderMonthText = calendarProps.renderMonthText; calendarProps.renderMonthElement; var restCalendarProps = __rest(calendarProps, ["onPrevMonthClick", "onNextMonthClick", "isDayHighlighted", "renderDayContents", "initialVisibleMonth", "renderMonthText", "renderMonthElement"]);
        var restDayPickerProps = {
            isDayBlocked: isDayBlocked,
            minimumNights: 0,
            isOutsideRange: function () { return false; },
        };
        return (React__default.createElement("div", { className: clsx(styles.DateRangeFilterInline, className) },
            React__default.createElement("div", { className: styles.DateRangeFilterControls },
                React__default.createElement("div", { className: styles.DateRangeFilterControls_row }, !!customDateRanges && Array.isArray(customDateRanges)
                    ? this.checkIfCustomArray().map(function (customRange, index) {
                        var checked = (selectedCustomRange === null || selectedCustomRange === void 0 ? void 0 : selectedCustomRange.name) ===
                            (customRange === null || customRange === void 0 ? void 0 : customRange.name);
                        return !!customRange &&
                            Object.keys(customRange).length ? (React__default.createElement(Radio, { key: "CustomDateRangeFilter-".concat(customRange.name, "-").concat(index), checked: checked, onChange: _this.handleCustomRangeDateChange(customRange), value: customRange.name, label: customRange.name === CUSTOM
                                ? rangeLabels[CUSTOM]
                                : customRange.name, className: styles.DateRangeFilterRadio })) : null;
                    })
                    : (isCustomRangeShowed
                        ? DATE_RANGE_FILTERS_WITH_CUSTOM
                        : DATE_RANGE_FILTERS).map(function (rangeFilter, index) { return (React__default.createElement(Radio, { key: "DateRangeFilter-".concat(rangeFilter, "-").concat(index), checked: selectedRange === rangeFilter, onChange: _this.handleDateChange, value: rangeFilter, label: rangeLabels[rangeFilter], className: styles.DateRangeFilterRadio })); }))),
            this.isCustomDatePickerRender() && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement("div", { className: styles.DateRangeFilterDesktop },
                    React__default.createElement("div", { className: styles.DateRangeFilterPickers },
                        React__default.createElement("div", { className: styles.DateRangeFilterPickers_picker },
                            React__default.createElement(Text, { type: "label" },
                                dateLabels['startDate'],
                                React__default.createElement("input", { "data-testid": 'custom-start-date-input', className: "".concat(styles.DateRangeFilterPickers_input, " ").concat(startDateInputInvalid
                                        ? styles.DateRangeFilterPickers_input___error
                                        : ''), value: startDateInput, onChange: this.handleStartDateInputChange, onFocus: this.handleStartDateInputFocus, ref: startDateInputRef })),
                            React__default.createElement(Text, { type: "label", className: styles.DateRangeFilterPickers_errorMessage }, startDateInputInvalid
                                ? invalidInputErrorMessage
                                : '')),
                        React__default.createElement("div", { className: styles.DateRangeFilterPickers_picker },
                            React__default.createElement(Text, { type: "label" },
                                dateLabels['endDate'],
                                React__default.createElement("input", { "data-testid": 'custom-end-date-input', className: "".concat(styles.DateRangeFilterPickers_input, " ").concat(endDateInputInvalid
                                        ? styles.DateRangeFilterPickers_input___error
                                        : ''), value: endDateInput, onChange: this.handleEndDateInputChange, onFocus: this.handleEndDateInputFocus, ref: endDateInputRef })),
                            React__default.createElement(Text, { type: "label", className: styles.DateRangeFilterPickers_errorMessage }, endDateInputInvalid
                                ? invalidInputErrorMessage
                                : ''))),
                    React__default.createElement("div", { className: "DateRangeFilterInline" },
                        React__default.createElement(DayPickerRangeController, __assign({}, restCalendarProps, restDayPickerProps, { startDate: startDate, endDate: endDate, focusedInput: focusedInput, numberOfMonths: 1, keepOpenOnDateSelect: true, renderDayContents: this.renderDayContents, hideKeyboardShortcutsPanel: true, navPrev: React__default.createElement("div", { className: styles.DateRangeFilterArrow, tabIndex: 0, "aria-label": "Move to previous month" },
                                React__default.createElement(Icon, { name: "ChevronLeftIcon", alt: "Previous Month", size: 24, className: styles.DateRangeFilterArrow_arrow })), navNext: React__default.createElement("div", { className: styles.DateRangeFilterArrow, tabIndex: 0, "aria-label": "Move to next month" },
                                React__default.createElement(Icon, { name: "ChevronRightIcon", alt: "Next Month", size: 24, className: styles.DateRangeFilterArrow_arrow })), renderMonthElement: this.renderMonthElement, initialVisibleMonth: null, onDatesChange: this.handleCustomDateChange, onFocusChange: this.handleFocusChange, renderMonthText: undefined, onPrevMonthClick: onPrevMonthClick, onNextMonthClick: onNextMonthClick, isDayHighlighted: isDayHighlighted })))),
                React__default.createElement("div", { className: styles.DateRangeFilterMobile },
                    React__default.createElement(DateRangePickerInline, __assign({}, restCalendarProps, restDayPickerProps, { startDate: startDate, endDate: endDate, onDatesChange: this.handleCustomDateChange, startDateLabel: dateLabels['startDate'], endDateLabel: dateLabels['endDate'], initialVisibleMonth: initialVisibleMonth, renderDayContents: renderDayContents, onPrevMonthClick: onPrevMonthClick, onNextMonthClick: onNextMonthClick, isDayHighlighted: isDayHighlighted, renderMonthText: renderMonthText })))))));
    };
    DateRangeFilterInline.displayName = 'DateRangeFilterInline';
    DateRangeFilterInline.propTypes = propTypes;
    DateRangeFilterInline.defaultProps = __assign({}, defaultProps);
    return DateRangeFilterInline;
}(React__default.Component));

export { DateRangeFilterInline as default };
